// T027: SharedSecrets edit component for updating metadata and files
// Feature: 010-ios-android-zenky
import {
    Edit,
    TextInput,
    DateInput,
    BooleanInput,
    TopToolbar,
    ListButton,
    TabbedForm,
    FormTab,
    useNotify,
    useRefresh,
    useDataProvider,
} from 'react-admin';
import { useState } from 'react';
import { Box, Button, Typography, Divider, Chip, Grid } from '@mui/material';
import {
    Upload as UploadIcon,
    CheckCircle as CheckCircleIcon,
    CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import type { LogtoDataProvider } from '../../dataProvider';

const EditActions = () => (
    <TopToolbar>
        <ListButton />
    </TopToolbar>
);

// Component for uploading iOS certificate (shared across all apps)
const IOSCertificateUpload = () => {
    const [iosCertFile, setIosCertFile] = useState<File | null>(null);
    const [certificatePassword, setCertificatePassword] = useState('');
    const [uploading, setUploading] = useState(false);

    const dataProvider = useDataProvider<LogtoDataProvider>();
    const notify = useNotify();
    const refresh = useRefresh();

    const handleUpload = async () => {
        if (!iosCertFile) {
            notify('Please select a certificate file', { type: 'error' });
            return;
        }

        if (!certificatePassword) {
            notify('Certificate password is required', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            await dataProvider.uploadSharedIOSCertificate({
                certificate: iosCertFile,
                certificate_password: certificatePassword,
            });

            notify('iOS certificate uploaded successfully', { type: 'success' });
            setIosCertFile(null);
            setCertificatePassword('');
            refresh();
        } catch (error) {
            notify('Failed to upload iOS certificate', { type: 'error' });
            console.error('iOS upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ mt: 3, mb: 2, p: 3, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CloudUploadIcon color="primary" />
                <Typography variant="h6">
                    Upload Shared iOS Certificate
                </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Upload shared Developer Certificate (.p12) used for signing all apps.
                Provisioning profiles are per-app and should be uploaded in each client's secrets.
            </Typography>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <input
                        accept=".p12,.pfx"
                        style={{ display: 'none' }}
                        id="shared-ios-certificate-file"
                        type="file"
                        onChange={(e) => e.target.files && setIosCertFile(e.target.files[0])}
                    />
                    <label htmlFor="shared-ios-certificate-file">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            fullWidth
                        >
                            Select Certificate (.p12)
                        </Button>
                    </label>
                    {iosCertFile && (
                        <Chip
                            label={iosCertFile.name}
                            onDelete={() => setIosCertFile(null)}
                            sx={{ mt: 1 }}
                            icon={<CheckCircleIcon />}
                            color="success"
                        />
                    )}
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <TextInput
                        source="uploadCertificatePassword"
                        label="Certificate Password"
                        type="password"
                        fullWidth
                        value={certificatePassword}
                        onChange={(e: any) => setCertificatePassword(e.target.value)}
                        helperText="Required for uploading new certificate"
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={!iosCertFile || uploading}
                        startIcon={<UploadIcon />}
                    >
                        {uploading ? 'Uploading...' : 'Upload iOS Certificate'}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

// Component for uploading App Store Connect API key
const AppStoreConnectKeyUpload = () => {
    const [keyFile, setKeyFile] = useState<File | null>(null);
    const [keyID, setKeyID] = useState('');
    const [issuerID, setIssuerID] = useState('');
    const [uploading, setUploading] = useState(false);

    const dataProvider = useDataProvider<LogtoDataProvider>();
    const notify = useNotify();
    const refresh = useRefresh();

    const handleUpload = async () => {
        if (!keyFile) {
            notify('Please select a key file', { type: 'error' });
            return;
        }

        if (!keyID || !issuerID) {
            notify('Key ID and Issuer ID are required', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            await dataProvider.uploadSharedAppStoreConnectKey({
                key: keyFile,
                key_id: keyID,
                issuer_id: issuerID,
            });

            notify('App Store Connect key uploaded successfully', { type: 'success' });
            setKeyFile(null);
            setKeyID('');
            setIssuerID('');
            refresh();
        } catch (error) {
            notify('Failed to upload App Store Connect key', { type: 'error' });
            console.error('App Store Connect key upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ mt: 3, mb: 2, p: 3, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CloudUploadIcon color="primary" />
                <Typography variant="h6">
                    Upload App Store Connect API Key
                </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Upload App Store Connect API key (.p8) for publishing to Apple App Store.
            </Typography>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <input
                        accept=".p8"
                        style={{ display: 'none' }}
                        id="shared-app-store-connect-key-file"
                        type="file"
                        onChange={(e) => e.target.files && setKeyFile(e.target.files[0])}
                    />
                    <label htmlFor="shared-app-store-connect-key-file">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            fullWidth
                        >
                            Select API Key (.p8)
                        </Button>
                    </label>
                    {keyFile && (
                        <Chip
                            label={keyFile.name}
                            onDelete={() => setKeyFile(null)}
                            sx={{ mt: 1 }}
                            icon={<CheckCircleIcon />}
                            color="success"
                        />
                    )}
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput
                        source="uploadKeyID"
                        label="Key ID"
                        fullWidth
                        value={keyID}
                        onChange={(e: any) => setKeyID(e.target.value)}
                        helperText="The Key ID from App Store Connect"
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextInput
                        source="uploadIssuerID"
                        label="Issuer ID"
                        fullWidth
                        value={issuerID}
                        onChange={(e: any) => setIssuerID(e.target.value)}
                        helperText="The Issuer ID from App Store Connect"
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={!keyFile || uploading}
                        startIcon={<UploadIcon />}
                    >
                        {uploading ? 'Uploading...' : 'Upload App Store Connect Key'}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

// Component for uploading Google Play service account JSON
const GooglePlayServiceAccountUpload = () => {
    const [jsonFile, setJsonFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const dataProvider = useDataProvider<LogtoDataProvider>();
    const notify = useNotify();
    const refresh = useRefresh();

    const handleUpload = async () => {
        if (!jsonFile) {
            notify('Please select a service account JSON file', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            await dataProvider.uploadSharedGooglePlayServiceAccount({
                service_account: jsonFile,
            });

            notify('Google Play service account uploaded successfully', { type: 'success' });
            setJsonFile(null);
            refresh();
        } catch (error) {
            notify('Failed to upload Google Play service account', { type: 'error' });
            console.error('Google Play service account upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ mt: 3, mb: 2, p: 3, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CloudUploadIcon color="primary" />
                <Typography variant="h6">
                    Upload Google Play Service Account
                </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Upload Google Play service account JSON for publishing to Google Play Store.
            </Typography>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <input
                        accept=".json"
                        style={{ display: 'none' }}
                        id="shared-google-play-service-account-file"
                        type="file"
                        onChange={(e) => e.target.files && setJsonFile(e.target.files[0])}
                    />
                    <label htmlFor="shared-google-play-service-account-file">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            fullWidth
                        >
                            Select Service Account JSON
                        </Button>
                    </label>
                    {jsonFile && (
                        <Chip
                            label={jsonFile.name}
                            onDelete={() => setJsonFile(null)}
                            sx={{ mt: 1 }}
                            icon={<CheckCircleIcon />}
                            color="success"
                        />
                    )}
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={!jsonFile || uploading}
                        startIcon={<UploadIcon />}
                    >
                        {uploading ? 'Uploading...' : 'Upload Google Play Service Account'}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export const SharedSecretsEdit = () => (
    <Edit
        mutationMode="pessimistic"
        actions={<EditActions />}
        title="Edit Shared Zenky Account Secrets"
    >
        <TabbedForm>
            <FormTab label="iOS Configuration">
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3, mt: 2 }}>
                    Shared iOS certificate used for signing all apps. Provisioning profiles are per-app and managed in each client's secrets.
                </Typography>

                {/* iOS Team Configuration */}
                <TextInput
                    source="teamId"
                    label="Team ID"
                    fullWidth
                    helperText="Apple Developer Team ID"
                />
                <TextInput
                    source="teamName"
                    label="Team Name"
                    fullWidth
                />
                <TextInput
                    source="itcTeamId"
                    label="App Store Connect Team ID"
                    fullWidth
                />
                <TextInput
                    source="itcTeamName"
                    label="App Store Connect Team Name"
                    fullWidth
                />

                {/* Certificate Metadata */}
                <TextInput
                    source="certificateId"
                    label="Certificate ID"
                    fullWidth
                />
                <DateInput
                    source="certificateExpiration"
                    label="Certificate Expiration"
                    fullWidth
                />

                {/* Auto-management */}
                <BooleanInput
                    source="autoManageCertificates"
                    label="Auto-Manage Certificates"
                    helperText="Automatically renew certificates before expiration"
                />

                {/* File Upload Section */}
                <IOSCertificateUpload />
            </FormTab>

            <FormTab label="Publishing Configuration">
                <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                    App Store Connect (iOS)
                </Typography>
                <TextInput
                    source="appStoreConnectKeyId"
                    label="App Store Connect Key ID"
                    fullWidth
                    disabled
                    helperText="Read-only. Set during key upload."
                />
                <TextInput
                    source="appStoreConnectIssuerId"
                    label="App Store Connect Issuer ID"
                    fullWidth
                    disabled
                    helperText="Read-only. Set during key upload."
                />
                {/* App Store Connect Key Upload */}
                <AppStoreConnectKeyUpload />

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ mb: 2 }}>
                    Google Play (Android)
                </Typography>
                {/* Google Play Service Account Upload */}
                <GooglePlayServiceAccountUpload />
            </FormTab>

            <FormTab label="System Information">
                <TextInput source="id" disabled fullWidth />
                <DateInput source="createdAt" disabled fullWidth />
                <DateInput source="updatedAt" disabled fullWidth />
                <DateInput source="rotatedAt" disabled fullWidth />
            </FormTab>
        </TabbedForm>
    </Edit>
);
